import React from 'react'
import { CutToSizeGroupModel, CutToSizeLogicModel } from './types/models'
import DropDown from './DropDown'
import SizeInput from './SizeInput'
import './assets/style.scss'
import CutToSizeLogic from './CutToSizeLogic'
import MobileGrid from './MobileGrid'

type Props = {
  groups: CutToSizeGroupModel[]
  dimension: string
  thickness: string
}

const CutToSizeGroups = ({ groups, dimension, thickness }: Props) => {
  const cutToSizeLogic: CutToSizeLogicModel = CutToSizeLogic({ groups, dimension, thickness })

  const thicknessColumn = (): React.JSX.Element => {
    if (cutToSizeLogic.validThicknesses.length > 1) {
      return (
        <td className='inputColumn thickness'>
          <DropDown values={cutToSizeLogic.validThicknesses} onValueChange={cutToSizeLogic.onThicknessChange} value={cutToSizeLogic.selectedThickness} />
          <span className='spacer'></span>
        </td>
      )
    } else {
      return (
        <td className='inputColumn thickness'>
          <p className='groupName'>{cutToSizeLogic.selectedThickness}</p>
        </td>
      )
    }
  }

  const columns = (): React.JSX.Element => {
    if (!cutToSizeLogic.hasProducts()) {
      let colSpan = cutToSizeLogic.constraints.length
      if (cutToSizeLogic.usesSize()) { colSpan += 1 }
      if (cutToSizeLogic.usesThickness()) { colSpan += 1 }
      return (
        <td colSpan={colSpan} className='noAvailability-note'>This product is not available at the moment</td>
      )
    }
    return (
      <>
        { cutToSizeLogic.usesSize() &&
          <td className='inputColumn cuttingToolSize'>
            <DropDown values={cutToSizeLogic.sortArray(Object.keys(cutToSizeLogic.currentGroup.dimensions))} onValueChange={cutToSizeLogic.onDimensionChange} value={cutToSizeLogic.selectedDimension} />
            <span className='spacer'></span>
          </td>
        }
        { cutToSizeLogic.usesThickness() && thicknessColumn() }
        {
          cutToSizeLogic.constraints.map((constraint) => {
            const isLength = constraint.field === cutToSizeLogic.LENGTH
            return (
              <td className='inputColumn sizeInput' key={`${cutToSizeLogic.selectedProduct?.name}-${constraint.field}-input`}>
                <SizeInput
                  sizeConstraint={constraint}
                  unit={isLength ? cutToSizeLogic.unitForLength : cutToSizeLogic.unitForWidth}
                  value={isLength ? cutToSizeLogic.length : cutToSizeLogic.width}
                  valid={cutToSizeLogic.calculateValidSize(isLength)}
                  min={isLength ? cutToSizeLogic.lengthMin : cutToSizeLogic.widthMin}
                  max={isLength ? cutToSizeLogic.lengthMax : cutToSizeLogic.widthMax}
                  step={isLength ? cutToSizeLogic.lengthStep : cutToSizeLogic.widthStep}
                  onSizeChange={cutToSizeLogic.onSizeChange}
                  onUnitChanged={cutToSizeLogic.onUnitChanged}
                />
              </td>
            )
          })
        }
      </>
    )
  }

  const submit = (): void => {
    if (cutToSizeLogic.isValid() && cutToSizeLogic.canSubmit) {
      const form = document.getElementsByClassName('add-to-basket-form')[0] as HTMLFormElement
      const event = new Event('submit', {
        bubbles: true,
        cancelable: true
      })
      form.dispatchEvent(event)
    }
  }

  return (
    <div className='cuttingTool-table-container'
        data-product={cutToSizeLogic.selectedProduct ? cutToSizeLogic.selectedProduct.id : 0}
        data-length={cutToSizeLogic.sizeInMM(cutToSizeLogic.length, cutToSizeLogic.unitForLength)}
        data-width={cutToSizeLogic.sizeInMM(cutToSizeLogic.width, cutToSizeLogic.unitForWidth)}
        data-quantity={cutToSizeLogic.quantity}
        data-valid={cutToSizeLogic.isValid() ? 1 : 0}
        data-cansubmit={cutToSizeLogic.canSubmit}>
      <table className='customCutting-table product-table product-list-table'>
        <thead>
          <tr>
            <th className='productTable-sidePadding' style={{ width: '10.78px' }}></th>
            <th className='cuttingTool-groupName cuttingTool-tableHeader'>
              <p>{`Custom Cut ${cutToSizeLogic.currentGroup.profile}`}</p>
            </th>
            { cutToSizeLogic.usesSize() &&
              <th className={'cuttingTool-tableHeader cuttingTool-sizeHeading cuttingTool-inputField'}>
                <p>Size</p>
              </th>
            }
            { cutToSizeLogic.usesThickness() &&
              <th className={'cuttingTool-tableHeader cuttingTool-thicknessHeading cuttingTool-inputField'}>
                <p>{cutToSizeLogic.currentGroup.useWeightPerMetre ? 'Weight (kg/m)' : 'Thickness'}</p>
              </th>
            }
            {
              cutToSizeLogic.constraints.map(({ field }) => (
                <th
                  key={`${cutToSizeLogic.selectedProduct?.name}-${field}-header`}
                  className={`cuttingTool-tableHeader cuttingTool-${field.toLowerCase()}Heading cuttingTool-inputField`}>
                    <p>{field}</p>
                </th>
              ))
            }
            <th className='cuttingTool-tableHeader'></th>
            {
              cutToSizeLogic.selectedProduct && cutToSizeLogic.pricingOption && cutToSizeLogic.pricingOption.originalPrice &&
              <th className='price ex-vat cuttingTool-price' scope='col'>
                Was<br/>
                <small>(Ex VAT)</small>
                <span className="on-offer">on offer</span>
              </th>
            }
            <th className='price ex-vat cuttingTool-price' scope='col'>
              Price<br/>
              <small>(Ex VAT)</small>
            </th>
            <th className='price inc-vat cuttingTool-price' scope='col'>
              Price<br/>
              <small>(Inc VAT)</small>
            </th>
            <th className='cuttingTool-quantity' scope='col'>
              Quantity
            </th>
            <th className='productTable-sidePadding' style={{ width: '10.78px' }}></th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className='productTable-sidePadding'></td>
            <td className='inputColumn'>
              { groups.length > 1
                ? (<DropDown values={groups.map((ctg) => ctg.name)} onValueChange={cutToSizeLogic.onGroupChange} value={cutToSizeLogic.currentGroup.name} />)
                : (<p className='groupName'>{cutToSizeLogic.currentGroup.name}</p>)
              }
            </td>
            {columns()}
            <td></td>
            {
              cutToSizeLogic.selectedProduct && cutToSizeLogic.pricingOption && cutToSizeLogic.pricingOption.originalPrice &&
              <td className='price inputColumn'>
                <span className='prev-price'>£{cutToSizeLogic.originalPrice.toFixed(2)}</span>
              </td>
            }
            <td className='price inputColumn'>
              <span className='cuttingTool-priceExVat'>£{cutToSizeLogic.price.toFixed(2)}</span>
            </td>
            <td className='price inputColumn vat-price'>
              <span className='cuttingTool-priceIncVat'>£{(cutToSizeLogic.price * cutToSizeLogic.VAT_RATE).toFixed(2)}</span>
            </td>
            <td className='inputColumn quantityInputColumn'>
              <input
              className='qty-input customCutting-field cuttingTool-quantityField'
              type='number'
              step='1'
              min='0'
              max='999'
              value={cutToSizeLogic.quantity || ''}
              autoComplete='off'
              onChange={cutToSizeLogic.onQuantityChanged}/>
            </td>
            <td className='productTable-sidePadding'></td>
          </tr>
        </tbody>
      </table>
      <div className='customCutting-grid'>
        <div>
          <p>{`Custom Cut ${cutToSizeLogic.currentGroup.profile}`}</p>
        </div>

        {MobileGrid({ groups, cutToSizeLogic })}

        <div className={`cuttingTool-addToBasket ${cutToSizeLogic.isValid() && cutToSizeLogic.canSubmit ? 'active' : 'inactive'}`}>
          <input type='submit' className='button' value='Add to Basket' role='button' aria-disabled='false' id='cut-to-size-submit-btn' onClick={submit}/>
        </div>
      </div>
    </div>
  )
}

export default CutToSizeGroups
